import { useEffect, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MainLayout } from './layouts';
import { useUIStore } from './stores/uiStore';
import { useAuth, type AccountType } from './hooks/useAuth';
import { Skeleton, ToastProvider, ToastContainer } from './components/ui';
import type { AppUser, AuthMode } from './services/datastore/types';
import type { OAuthProvider } from './services/auth';

// Lazy-loaded page components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const SongListPage = lazy(() => import('./pages/SongListPage').then(m => ({ default: m.SongListPage })));
const SongDetailPage = lazy(() => import('./pages/SongDetailPage').then(m => ({ default: m.SongDetailPage })));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage').then(m => ({ default: m.DiscoverPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const SpotifyImportPage = lazy(() => import('./pages/SpotifyImportPage').then(m => ({ default: m.SpotifyImportPage })));
const SharedListPage = lazy(() => import('./pages/SharedListPage').then(m => ({ default: m.SharedListPage })));

// Auth callbacks are smaller and loaded less frequently, but still lazy
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage').then(m => ({ default: m.AuthCallbackPage })));
const SpotifyCallbackPage = lazy(() => import('./pages/SpotifyCallbackPage').then(m => ({ default: m.SpotifyCallbackPage })));

// Auth context to share user state across the app
interface AuthContextValue {
  user: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  mode: AuthMode;
  /** Whether the account is offline-only or linked to OAuth */
  accountType: AccountType;
  /** The OAuth provider if linked (google/apple) */
  provider: OAuthProvider | null;
  /** Start offline mode - only call on explicit user action */
  startOffline: () => Promise<{ error: Error | null }>;
  /** Sign out and clear local data */
  signOut: () => Promise<{ error: Error | null }>;
  /** Sign in with Google OAuth */
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  /** Sign in with Apple OAuth */
  signInWithApple: () => Promise<{ error: Error | null }>;
  /** Link current offline account with Google */
  linkGoogleAccount: () => Promise<{ error: Error | null }>;
  /** Link current offline account with Apple */
  linkAppleAccount: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  mode: 'offline',
  accountType: 'offline',
  provider: null,
  startOffline: async () => ({ error: new Error('Not initialized') }),
  signOut: async () => ({ error: new Error('Not initialized') }),
  signInWithGoogle: async () => ({ error: new Error('Not initialized') }),
  signInWithApple: async () => ({ error: new Error('Not initialized') }),
  linkGoogleAccount: async () => ({ error: new Error('Not initialized') }),
  linkAppleAccount: async () => ({ error: new Error('Not initialized') }),
});

export const useAuthContext = () => useContext(AuthContext);

/**
 * Loading screen shown while auth initializes
 */
function AuthLoading() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '1rem',
      padding: '2rem',
    }}>
      <Skeleton variant="circular" width={64} height={64} />
      <Skeleton variant="text" width={200} height={24} />
      <Skeleton variant="text" width={150} height={16} />
    </div>
  );
}

/**
 * Loading fallback for lazy-loaded pages
 */
function PageLoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      gap: '1rem',
      padding: '2rem',
    }}>
      <Skeleton variant="rectangular" width="100%" height={48} style={{ maxWidth: '400px' }} />
      <Skeleton variant="text" width="60%" height={20} />
      <Skeleton variant="rectangular" width="100%" height={120} style={{ maxWidth: '400px' }} />
    </div>
  );
}

/**
 * Root application component.
 * Sets up routing, authentication, and theme management.
 */
function App() {
  const theme = useUIStore((state) => state.theme);
  const {
    user,
    isLoading,
    isAuthenticated,
    mode,
    error,
    accountType,
    provider,
    startOffline,
    signOut,
    signInWithGoogle,
    signInWithApple,
    linkGoogleAccount,
    linkAppleAccount,
  } = useAuth();

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // Show loading while auth initializes
  if (isLoading) {
    return <AuthLoading />;
  }

  // Show error if auth failed (but continue - may work offline)
  if (error) {
    console.error('Auth error:', error);
  }

  const authValue: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    mode,
    accountType,
    provider,
    startOffline,
    signOut,
    signInWithGoogle,
    signInWithApple,
    linkGoogleAccount,
    linkAppleAccount,
  };

  return (
    <AuthContext.Provider value={authValue}>
      <BrowserRouter>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route element={<MainLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/songs" element={<SongListPage />} />
              <Route path="/songs/:id" element={<SongDetailPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/spotify/import" element={<SpotifyImportPage />} />
              <Route path="/shared/:shareToken" element={<SharedListPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
            </Route>
            {/* OAuth callback route - outside MainLayout for cleaner UX */}
            <Route
              path="/auth/callback"
              element={
                <ToastProvider>
                  <AuthCallbackPage />
                  <ToastContainer />
                </ToastProvider>
              }
            />
            {/* Spotify OAuth callback route */}
            <Route
              path="/auth/spotify/callback"
              element={
                <ToastProvider>
                  <SpotifyCallbackPage />
                  <ToastContainer />
                </ToastProvider>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
